import React, {useEffect, useMemo, useState} from 'react';
import {EBirdObservationsByLocation} from "ebird-mydata-reader";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faAddressCard, faLeaf, faMapLocationDot} from "@fortawesome/free-solid-svg-icons";
import {getRandomDashboardHeroImage} from "../helpers/dashboardHeroImages";
import type {HomeLocation} from "../App";
import InfoIcon from "../components/info-icon";
import type {EnrichedObservationsByLocation, LocationEcoregionData} from "../helpers/types";
import './data-onboarding.scss';

type SetHomeLocationProps = {
    observationsByLocation: Array<EBirdObservationsByLocation | EnrichedObservationsByLocation>,
    selectedHomeLocation?: HomeLocation,
    onSetHomeLocation: (location: HomeLocation) => void,
    onSkipHomeLocation: () => void
}

type HomeLocationOption = HomeLocation & {
    checklistCount: number,
    speciesCount: number,
    ecoData?: LocationEcoregionData
}

const getChecklistId = (observation: any) => {
    return observation.submissionID || observation.submissionId || `${observation.date}-${observation.time}-${observation.location}`;
}

const getSpeciesKey = (observation: any) => {
    return observation.taxonomicOrder?.toString() || observation.commonName || observation.scientificName || '';
}

const getCountryCode = (stateProvince?: string) => stateProvince?.split('-')[0] ?? '';

const getLocationOption = (locationGroup: EBirdObservationsByLocation | EnrichedObservationsByLocation): HomeLocationOption | undefined => {
    const firstObservation = locationGroup.observations.find(observation => observation.latitude && observation.longitude) ?? locationGroup.observations[0];
    if (!firstObservation?.location) return undefined;

    return {
        locationId: firstObservation.locationId || locationGroup.locationId || firstObservation.location,
        location: firstObservation.location || locationGroup.location,
        county: firstObservation.county || '',
        stateProvince: firstObservation.stateProvince || '',
        countryCode: getCountryCode(firstObservation.stateProvince),
        latitude: Number(firstObservation.latitude) || 0,
        longitude: Number(firstObservation.longitude) || 0,
        checklistCount: new Set(locationGroup.observations.map(getChecklistId).filter(Boolean)).size,
        speciesCount: new Set(locationGroup.observations.map(getSpeciesKey).filter(Boolean)).size,
        ecoData: 'ecoData' in locationGroup ? locationGroup.ecoData : undefined
    };
}

export default function SetHomeLocation({observationsByLocation, selectedHomeLocation, onSetHomeLocation, onSkipHomeLocation}: SetHomeLocationProps) {
    const onboardingHeroImage = useMemo(() => getRandomDashboardHeroImage(), []);
    const options = useMemo(() => {
        return observationsByLocation
            .map(getLocationOption)
            .filter((option): option is HomeLocationOption => Boolean(option))
            .sort((a, b) => {
                if (b.checklistCount !== a.checklistCount) return b.checklistCount - a.checklistCount;
                if (b.speciesCount !== a.speciesCount) return b.speciesCount - a.speciesCount;
                return a.location.localeCompare(b.location);
            });
    }, [observationsByLocation]);
    const [selectedLocationId, setSelectedLocationId] = useState(selectedHomeLocation?.locationId ?? options[0]?.locationId ?? '');
    const selectedOption = options.find(option => option.locationId === selectedLocationId) ?? options[0];
    const selectedRegion = selectedOption
        ? [selectedOption.county, selectedOption.stateProvince, selectedOption.countryCode].filter(Boolean).join(', ') || 'Unknown region'
        : '';
    const selectedCoordinates = selectedOption
        ? `${selectedOption.latitude.toFixed(4)}, ${selectedOption.longitude.toFixed(4)}`
        : '';
    const selectedBiome = selectedOption?.ecoData?.biomeName;

    useEffect(() => {
        if (!selectedLocationId && options[0]?.locationId) {
            setSelectedLocationId(options[0].locationId);
        }
    }, [options, selectedLocationId]);

    const handleSubmit = () => {
        if (!selectedOption) return;

        const {checklistCount, speciesCount, ecoData, ...homeLocation} = selectedOption;
        onSetHomeLocation(homeLocation);
    }

    return (
        <section className="data-onboarding" aria-labelledby="home-location-title">
            <div className="data-onboarding__content">
                <p className="data-onboarding__eyebrow">Home Location</p>
                <h1 className="data-onboarding__headline" id="home-location-title">Where does your birding story start?</h1>
                <div className="data-onboarding__copy">
                    <p>Choose a familiar location from your eBird history. BirdMap Explorer will use its county, state or province, country, and coordinates to personalize your Life Journey.</p>
                    <p>If you don't want to set a home location, you can skip this.</p>
                </div>

                {selectedOption ?
                    <>
                        <label className="data-onboarding__field">
                            <span>Home birding location</span>
                            <select value={selectedLocationId} onChange={event => setSelectedLocationId(event.target.value)}>
                                {options.map(option => (
                                    <option value={option.locationId} key={option.locationId}>
                                        {option.location} ({option.checklistCount.toLocaleString()} checklists)
                                    </option>
                                ))}
                            </select>
                        </label>

                        <dl className="data-onboarding__home-summary">
                            <div>
                                <dt>
                                    <FontAwesomeIcon icon={faAddressCard} aria-hidden="true" />
                                    <span>Region</span>
                                </dt>
                                <dd>{selectedRegion}</dd>
                            </div>
                            <div>
                                <dt>
                                    <FontAwesomeIcon icon={faMapLocationDot} aria-hidden="true" />
                                    <span>Coordinates</span>
                                </dt>
                                <dd>{selectedCoordinates}</dd>
                            </div>
                            {selectedBiome ?
                                <div>
                                    <dt>
                                        <FontAwesomeIcon icon={faLeaf} aria-hidden="true" />
                                        <span>Biome</span>
                                    </dt>
                                    <dd>{selectedBiome}</dd>
                                </div>
                                : null
                            }
                        </dl>

                        <div className="data-onboarding__actions">
                            <button type="button" className="btn btn-primary" onClick={handleSubmit}>
                                Set home location
                            </button>
                            <button type="button" className="btn btn-secondary" onClick={onSkipHomeLocation}>
                                Skip for now
                            </button>
                        </div>
                    </> :
                    <>
                        <div className="data-onboarding__copy">
                            <p>No locations were available in this data file. You can continue without setting a home location.</p>
                        </div>
                        <div className="data-onboarding__actions">
                            <button type="button" className="btn btn-primary" onClick={onSkipHomeLocation}>
                                Continue
                            </button>
                        </div>
                    </>
                }

                <div className="data-onboarding__privacy">
                    <InfoIcon className="data-onboarding__privacy-icon" />
                    <span>
                        <strong>Your home location stays local.</strong>
                        <span>This preference is stored only in your browser alongside your eBird data.</span>
                    </span>
                </div>
            </div>
            <aside
                className="data-onboarding__hero"
                aria-label="Bird in a natural habitat"
                style={{'--data-onboarding-hero-image': `url("${onboardingHeroImage}")`} as React.CSSProperties}
            >
                <div className="data-onboarding__hero-card">
                    <div className="data-onboarding__feature">
                        <span className="data-onboarding__feature-marker" aria-hidden="true">1</span>
                        <span>
                            <strong className="data-onboarding__feature-title">Pick a familiar place</strong>
                            <span className="data-onboarding__feature-copy">Your most-used eBird locations are listed first, so home or local patches should be easy to find.</span>
                        </span>
                    </div>
                    <div className="data-onboarding__feature">
                        <span className="data-onboarding__feature-marker" aria-hidden="true">2</span>
                        <span>
                            <strong className="data-onboarding__feature-title">Confirm your region</strong>
                            <span className="data-onboarding__feature-copy">We derive county, state or province, country, and coordinates from your own checklist data.</span>
                        </span>
                    </div>
                    <div className="data-onboarding__feature">
                        <span className="data-onboarding__feature-marker" aria-hidden="true">3</span>
                        <span>
                            <strong className="data-onboarding__feature-title">Personalize Your Life Journey</strong>
                            <span className="data-onboarding__feature-copy">Home context unlocks richer story beats about local birding and trips away from home.</span>
                        </span>
                    </div>
                </div>
            </aside>
        </section>
    );
}
